import { Effect, Schema as S } from "effect";
import * as Command from "foldkit/command";
import type * as Update from "foldkit/update";
import type { Api } from "../pages/shared";
import { Action as EditorAction } from "../widgets/editor";
import { track } from "./analytics";
import { interactionActions } from "./interactionActions";
import { Message } from "./message";
import type { Model } from "./model";
import { type RouteTag, urlToRoute } from "./routes";

type Event = Readonly<{ event: string; props: Record<string, string> }>;
type Tagged = Readonly<{ _tag: string; message?: unknown }>;
const snake = (value: string) =>
	value.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
const isAction = (tag: string): tag is keyof typeof interactionActions =>
	Object.hasOwn(interactionActions, tag);
const trackedTabs = new Set(["content", "history", "entries"]);
const tagged = (value: unknown): value is Tagged =>
	typeof value === "object" &&
	value !== null &&
	"_tag" in value &&
	typeof value._tag === "string";

/** Schema tags select event names; only vetted control enums become properties. */
const fromMessage = (
	message: Tagged,
	page: string,
	widgets: ReadonlyArray<string>,
): Event | null => {
	if (tagged(message.message)) {
		const widget = message._tag.replace(/^Got/, "").replace(/Message$/, "");
		return fromMessage(message.message, page, [...widgets, snake(widget)]);
	}
	const action = isAction(message._tag)
		? interactionActions[message._tag]
		: undefined;
	const control =
		message._tag === "ClickedEditorAction" &&
		"action" in message &&
		S.is(EditorAction)(message.action)
			? { control: message.action }
			: message._tag === "SelectedTab" &&
					"tab" in message &&
					typeof message.tab === "string" &&
					trackedTabs.has(message.tab)
				? { control: message.tab }
				: {};
	return action
		? {
				event: [page, ...widgets, action].join("_"),
				props: { page, widget: widgets.join("_") || "page", ...control },
			}
		: null;
};

export const interactionEvent = (
	message: Message,
	route: RouteTag,
): Event | null => {
	const page = snake(route);
	if (message._tag === "ClickedLink") {
		const internal = message.request._tag === "Internal";
		return {
			event: "navigation_link_clicked",
			props: {
				page,
				kind: internal ? "internal" : "external",
				destination:
					message.request._tag === "Internal"
						? snake(urlToRoute(message.request.url)._tag)
						: "external",
			},
		};
	}
	if (message._tag === "GotPageMessage")
		return fromMessage(
			message.message.message,
			snake(message.message._tag),
			[],
		);
	return fromMessage(message, page, []);
};

/** A typing burst reports an edit at most once per second; discrete actions always pass. */
const textChanges = [
	"amount",
	"attribute_key",
	"attribute_value",
	"body",
	"description",
	"email",
	"input",
	"key",
	"key_description",
	"label",
	"name",
	"notes",
	"query",
	"search",
	"search_text",
	"subtitle",
	"text",
	"title",
	"topup_amount",
	"value",
].map((field) => `_${field}_changed`);
export const makeInteractionTracker = (
	capture: typeof track,
	now: () => number = Date.now,
): typeof track => {
	const lastCaptured = new Map<string, number>();
	return (event, props) => {
		if (textChanges.some((suffix) => event.endsWith(suffix))) {
			const at = now();
			const previous = lastCaptured.get(event);
			if (previous !== undefined && at >= previous && at - previous < 1000)
				return;
			lastCaptured.set(event, at);
		}
		capture(event, props);
	};
};
const captureInteraction = makeInteractionTracker(track);
export const TrackInteraction = Command.define("TrackInteraction", {
	args: { event: S.String, props: S.Record(S.String, S.String) },
	messages: [Message.CompletedTrackInteraction],
	execute: ({ event, props }) =>
		Effect.sync(() => captureInteraction(event, props)).pipe(
			Effect.as(Message.CompletedTrackInteraction()),
		),
});
type AppUpdate = (
	model: Model,
	message: Message,
) => Update.Return<Model, Message, Api>;

/** Runtime boundary: append an Effect command; reducers remain pure and replayable. */
export const withInteractionAnalytics =
	(update: AppUpdate): AppUpdate =>
	(model, message) => {
		const result = update(model, message);
		const event = interactionEvent(message, model.route._tag);
		return event
			? {
					...result,
					commands: [...(result.commands ?? []), TrackInteraction(event)],
				}
			: result;
	};
